import { useOnboardingResearchWatcher } from '@/containers/agents/use-onboarding-research-watcher.hook';
import { useAttioConnectionRefresh } from '@/containers/integrations/use-attio-connection-refresh.hook';
import { useCreditSync } from '@/hooks/use-credit-sync.hook';
import {
  getPageKey,
  pingIntercom,
  trackIntercomEvent,
  updateIntercomUser,
} from '@/hooks/use-intercom.hook';
import { useLeadListUpdateRefresh } from '@/hooks/use-unified-websocket.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { useGetFeatureAccessQuery } from '@/store/features/billing.feature';
import { useAuth, useOrganization, useUser } from '@clerk/react';
import Clarity from '@microsoft/clarity';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { PaidAccessDialog } from '@/containers/settings/billing/paid-access-dialog.component';

/** The organization PostHog has registered as this user's group, if any. */
function registeredOrganization(
  posthog: ReturnType<typeof usePostHog>
): unknown {
  try {
    return posthog.getGroups?.()?.organization;
  } catch {
    return undefined;
  }
}

/**
 * Tracking & onboarding logic isolated in a non-rendering component.
 * Clerk / PostHog hooks live here so their state changes never
 * cascade re-renders through the layout tree.
 */
export function AppEffects() {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const { userId } = useAuth();
  const { organization, membership } = useOrganization();
  useCreditSync(organization?.id ?? '');
  useAttioConnectionRefresh(organization?.id ?? '');
  // Multi-org: switching orgs (our switcher, Clerk's, accepting an invite)
  // reloads the app onto the new org, so no cache, socket, Redux slice or
  // extension login keeps serving the old one, and nobody stays on its pages.
  const orgIdRef = useRef(organization?.id);
  useEffect(() => {
    const previous = orgIdRef.current;
    orgIdRef.current = organization?.id;
    if (previous && organization?.id && previous !== organization.id) {
      window.location.assign('/');
    }
  }, [organization?.id]);
  // App-shell-level refresh authority for list mutations. Without this, Copilot
  // ops from the home view (which never mounts the per-list-page leads handler)
  // leave the sidebar list index / companies / target-list leads stale until a
  // hard refresh. Mounted once here so it survives navigation.
  useLeadListUpdateRefresh();
  // App-wide watcher for the agents-onboarding research run: while the
  // localStorage in-flight mark exists it polls GET /agents/profile and, on
  // completion, fires the one-shot "profile ready" toast + Confirm CTA —
  // wherever the user has navigated to in the meantime.
  useOnboardingResearchWatcher();
  const { user, isSignedIn, isLoaded: isUserLoaded } = useUser();
  // Entitlement signals for the current org. Used to stamp org-group
  // properties in PostHog (below) so operational flags (e.g.
  // `review_credits_v1`) can target AppSumo orgs, and winback campaigns can
  // target trials. Skipped until there's an org to key it on.
  // `currentData`, not `data`: on an org switch RTK Query keeps serving the
  // PREVIOUS argument's last successful result until the new request settles,
  // while the effect below already reacts to the new organization id. Using
  // `data` therefore stamps the new org's PostHog group with the old org's
  // `is_appsumo` / `appsumo_tier` / trial properties — wrong cohorts, and wrong
  // same-load flag evaluation. Every other org-scoped billing consumer here
  // reads `currentData` for this reason.
  const { currentData: featureAccess } = useGetFeatureAccessQuery(
    { organizationId: organization?.id ?? '' },
    { skip: !isSignedIn || !organization?.id }
  );
  const groupedOrgIdRef = useRef<string | null>(null);
  // The org group last sent to PostHog: its org and the properties with it.
  // Every group() call with properties makes PostHog reload feature flags, and
  // each entitlements response is a new object (its `as_of` is the server's
  // clock), so the group goes out only when this key changes.
  const groupedKeyRef = useRef<string | null>(null);
  const isAppsumo = featureAccess?.is_appsumo;
  const appsumoTier = featureAccess?.appsumo_tier ?? undefined;
  const isTrialing = featureAccess?.trial ? featureAccess.trial.active : null;
  const trialEndsAt = featureAccess?.trial ? featureAccess.trial.ends_at : null;
  const isOnboardingRoute =
    typeof window !== 'undefined' && window.location.pathname === '/onboarding';

  // Fire-and-forget analytics identity. Deliberately NOT a render gate: no
  // analytics outcome may delay or block the app (the trial-era analytics
  // boundary held every org's boot on this and is gone for that reason).
  useEffect(() => {
    if (!isSignedIn || !user || !posthog) return;

    // Reset before re-identifying so a previous org's $group_0 doesn't leak
    // onto events captured after an org switch. Must run before identify so
    // the user properties from this pass aren't wiped by the reset.
    if (
      organization?.id &&
      groupedOrgIdRef.current &&
      groupedOrgIdRef.current !== organization.id
    ) {
      bestEffortAnalytics(() => posthog.reset());
      groupedOrgIdRef.current = null;
      groupedKeyRef.current = null;
    }

    bestEffortAnalytics(() =>
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username,
        created_at: user.createdAt,
        firstName: user.firstName,
        lastName: user.lastName,
        userId,
        organizationId: organization?.id,
        organizationName: organization?.name,
        role: membership?.role,
      })
    );

    if (organization?.id) {
      const groupProperties = {
        name: organization.name ?? undefined,
        // Client-set signals so org-keyed operational flags can target these
        // cohorts on the SAME load (posthog-js forwards group properties on
        // the flag-eval request). `appsumo_tier` is omitted (not null) for
        // non-AppSumo orgs so an `is_set` condition correctly excludes them.
        ...(isAppsumo !== undefined
          ? { is_appsumo: isAppsumo, appsumo_tier: appsumoTier }
          : {}),
        ...(isTrialing !== null
          ? { is_trialing: isTrialing, trial_ends_at: trialEndsAt }
          : {}),
      };
      const groupKey = JSON.stringify([organization.id, groupProperties]);
      if (
        groupedKeyRef.current !== groupKey ||
        registeredOrganization(posthog) !== organization.id
      ) {
        bestEffortAnalytics(() =>
          posthog.group('organization', organization.id, groupProperties)
        );
        // Kept only once PostHog holds the group: a call made before the SDK
        // has loaded does nothing, and the next pass must send it again.
        groupedKeyRef.current =
          registeredOrganization(posthog) === organization.id ? groupKey : null;
      }
      groupedOrgIdRef.current = organization.id;
    }

    bestEffortAnalytics(() => Clarity.identify(user.id));
  }, [
    posthog,
    user,
    isSignedIn,
    userId,
    organization?.id,
    organization?.name,
    membership?.role,
    // The signals the group carries, not the whole payload: each response is
    // a new object (`as_of` changes every time), which re-ran this effect and
    // reloaded PostHog's flags on every entitlements refetch.
    isAppsumo,
    appsumoTier,
    isTrialing,
    trialEndsAt,
  ]);

  useEffect(() => {
    if (!posthog) return;
    if (!isSignedIn && groupedOrgIdRef.current) {
      bestEffortAnalytics(() => posthog.reset());
      groupedOrgIdRef.current = null;
      groupedKeyRef.current = null;
    }
  }, [posthog, isSignedIn]);

  useEffect(() => {
    if (!isUserLoaded || !isSignedIn || isOnboardingRoute) {
      return;
    }

    const isOnboarded = user?.unsafeMetadata.completed_onboarding === true;
    const hasOrganizationContext = Boolean(organization?.id);
    const shouldForceOnboarding = !isOnboarded && !hasOrganizationContext;

    if (shouldForceOnboarding) {
      navigate({ to: '/onboarding', replace: true });
    }
  }, [
    isUserLoaded,
    isSignedIn,
    isOnboardingRoute,
    organization?.id,
    user?.unsafeMetadata.completed_onboarding,
    navigate,
  ]);

  // Ping Intercom on route changes so it tracks page views and checks for new messages.
  // When the route maps to a known page_key, set `last_page_key` so Intercom Outbound
  // Chats can target proactive Fin greetings against the attribute instead of URL globs.
  useEffect(() => {
    const pageKey = getPageKey(location.pathname);
    if (pageKey) {
      updateIntercomUser({ last_page_key: pageKey });
      trackIntercomEvent('viewed_page', { page_key: pageKey });
    } else {
      pingIntercom();
    }
  }, [location.pathname]);

  return <PaidAccessDialog organizationId={organization?.id} />;
}
